import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SecurityMetrics {
  totalIssues: number;
  criticalIssues: number;
  warnings: number;
  tablesWithoutRLS: number;
  securityScore: number;
}

interface PerformanceMetrics {
  averageResponseTime: number;
  activeConnections: number;
  memoryUsage: number;
  indexEfficiency: number;
}

interface SystemStats {
  employees: number;
  clients: number;
  bookings: number;
  tasks: number;
  notifications: number;
  hotels: number;
  companies: number;
  lastUpdated: string;
}

interface SystemHealth {
  security: SecurityMetrics;
  performance: PerformanceMetrics;
  stats: SystemStats;
}

export const useSystemHealth = () => {
  const [systemHealth, setSystemHealth] = useState<SystemHealth>({
    security: {
      totalIssues: 24,
      criticalIssues: 18,
      warnings: 6,
      tablesWithoutRLS: 2,
      securityScore: 85
    },
    performance: {
      averageResponseTime: 245,
      activeConnections: 12,
      memoryUsage: 68,
      indexEfficiency: 92
    },
    stats: {
      employees: 0,
      clients: 0,
      bookings: 0,
      tasks: 0,
      notifications: 0,
      hotels: 0,
      companies: 0,
      lastUpdated: new Date().toISOString()
    }
  });

  const [isLoading, setIsLoading] = useState(false);

  const fetchSystemStats = async () => {
    setIsLoading(true);
    try {
      // استعلام موحد للحصول على جميع الإحصائيات
      const { data: dashboardStats, error } = await supabase
        .rpc('get_dashboard_stats');

      if (error) {
        console.error('Error fetching dashboard stats:', error);
        toast.error('فشل في تحميل إحصائيات النظام');
        return;
      }

      if (dashboardStats && typeof dashboardStats === 'object') {
        const stats = dashboardStats as any;
        setSystemHealth(prev => ({
          ...prev,
          stats: {
            employees: stats.employees?.total || 0,
            clients: stats.clients?.total || 0,
            bookings: stats.bookings?.total || 0,
            tasks: stats.tasks?.total || 0,
            notifications: 0,
            hotels: stats.hotels?.total || 0,
            companies: 0,
            lastUpdated: new Date().toISOString()
          }
        }));
      }
    } catch (error) {
      console.error('Error in fetchSystemStats:', error);
      toast.error('خطأ في تحميل بيانات النظام');
    } finally {
      setIsLoading(false);
    }
  };

  const checkPerformanceMetrics = async () => {
    try {
      const startTime = performance.now();
      
      await supabase.from('employees').select('count').limit(1);
      
      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);

      setSystemHealth(prev => ({
        ...prev,
        performance: {
          ...prev.performance,
          averageResponseTime: responseTime
        }
      }));
    } catch (error) {
      console.error('Error checking performance:', error);
    }
  };

  const refreshAllMetrics = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchSystemStats(),
        checkPerformanceMetrics()
      ]);
      
      toast.success('تم تحديث جميع المقاييس بنجاح');
    } catch (error) {
      console.error('Error refreshing metrics:', error);
      toast.error('فشل في تحديث بعض المقاييس');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemStats();
    checkPerformanceMetrics();

    const interval = setInterval(() => {
      fetchSystemStats();
      checkPerformanceMetrics();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return {
    systemHealth,
    isLoading,
    refreshAllMetrics,
    fetchSystemStats,
    checkPerformanceMetrics
  };
};